import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Common } from '@trimblemaps/trimblemaps-js';
import { cloneDeep } from 'lodash-es';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { switchMap, take } from 'rxjs';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import {
  ProfileVehicleType,
  Trip,
  VehicleDimUnits,
} from 'src/app/_models/daws/trip';
import {
  RoutingProfileModel,
  VehicleDimensionsThesholdsModel,
  VehicleTypeEnum,
} from 'src/app/_models/profilews/profilews';
import { UserInfo } from 'src/app/auth/models/user';
import { AlertService } from 'src/app/auth/services/alert.service';
import { rpNameValidator } from 'src/app/route-profiles/validators/rp-name-validator';
import { ConversionService } from 'src/app/services/conversion.service';
import { UserService } from 'src/app/services/user.service';
import { RoutingProfileService } from 'src/app/services/vehicle-routing-profile.service';

@Component({
  selector: 'open-legacy-route',
  templateUrl: './open-legacy-route.component.html',
})
export class OpenLegacyRouteComponent implements OnInit, OnDestroy {
  tripData: any;
  data: Trip;
  CustomProfileName: FormControl;
  savedRouteName: string;
  existingProfileName: string;
  existingVehicleType: string;
  errorMessage: string;
  userInfo: UserInfo;
  isEuRegion: boolean;
  isLoading: boolean;
  isCreateProfile = false;
  isSaving = false;
  exceedVehicleType: string;
  subscriptionManager = new SubscriptionManager();
  drawRouteEvent: EventEmitter<Trip> = new EventEmitter();

  constructor(
    private profileService: RoutingProfileService,
    private bsModalRef: BsModalRef,
    private conversionService: ConversionService,
    private alertService: AlertService,
    private userService: UserService
  ) {}
  ngOnInit(): void {
    this.CustomProfileName = new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(64),
      rpNameValidator,
    ]);
    this.data = cloneDeep(this.tripData.dataValue);
    this.isLoading = true;
    this.savedRouteName = this.data?.Name;
    this.existingProfileName = this.data?.Profile?.name;
    this.existingVehicleType =
      this.conversionService.convertToProfileVehicleTypeEnum(
        this.tripData?.dataValue?.Profile?.vehicleType
      );
    this.userInfo = this.userService.getUser();
    this.isEuRegion = this.userInfo?.region?.toLowerCase() === Common.Region.EU;
    this.matchProfile(this.isEuRegion);
  }
  createProfile() {
    this.isCreateProfile = true;
  }
  useExitingProfile() {
    this.drawRouteEvent.emit(this.data);
    this.bsModalRef.hide();
  }
  saveNewProfile() {
    this.isSaving = true;
    this.errorMessage = '';
    const routingProfileId = this.profileService.getProfileId(
      this.data.Profile.name
    );
    const profile = this.profileService.getProfile(routingProfileId);
    this.data.Profile.name = this.CustomProfileName.value;

    const routeProfileOption =
      this.conversionService.convertToRoutingProfileModal(
        this.data,
        profile,
        this.isEuRegion
      );
    this.subscriptionManager.manage = this.profileService
      .createCustomProfile(routeProfileOption)
      .pipe(
        switchMap(async () =>
          this.profileService.retrieveAllProfilesSubscrition()
        )
      )
      .subscribe(
        () => {
          setTimeout(() => {
            this.isSaving = false;
            this.tripData.dataValue.Profile.name = this.data?.Profile?.name;
            this.tripData.dataValue.Profile.vehicleType =
              this.data?.Profile?.vehicleType;
            this.drawRouteEvent.emit(this.tripData.dataValue);
            this.alertService.success(
              'Profile created Successfully',
              true,
              'app-modal-alert'
            );
            this.bsModalRef.hide();
          }, 3000);
        },
        (error) => {
          this.isSaving = false;
          this.errorMessage = error?.error[0]?.message
            ? error?.error[0]?.message
            : 'Facing issue with profile creation';
        }
      );
  }
  checkProfileDimensionsLimit() {
    const profileVehicleType =
      this.conversionService.convertToProfileVehicleTypeEnum(
        this.data?.Profile?.vehicleType
      );

    const profileDim: VehicleDimensionsThesholdsModel = this.profileService
      .getProfileDimensionsMinMax()
      .find((profileData) => profileData?.vehicleType === profileVehicleType);
    if (
      this.data?.Profile.maxHeight > profileDim?.vehicleDimensionMax.height ||
      this.data?.Profile.totalWeight > profileDim?.vehicleDimensionMax.weight ||
      this.data?.Profile.maxWidth > profileDim?.vehicleDimensionMax.width ||
      this.data?.Profile.totalLength > profileDim?.vehicleDimensionMax.length
    ) {
      if (this.data?.Profile?.vehicleType === ProfileVehicleType.LightDuty) {
        this.data.Profile.vehicleType = ProfileVehicleType.MidsizeTruck;
        this.exceedVehicleType =
          this.conversionService.convertToProfileVehicleTypeEnum(
            this.data?.Profile?.vehicleType
          );
        const availableProfileByVehicleType =
          this.profileService.getAvailableProfileByVehicleType(
            this.data?.Profile?.vehicleType
          );
        this.data.Profile.name = availableProfileByVehicleType?.name;
      }
    } else if (
      (this.data?.Profile.maxHeight < profileDim?.vehicleDimensionMin.height ||
        this.data?.Profile.totalWeight <
          profileDim?.vehicleDimensionMin.weight ||
        this.data?.Profile.maxWidth < profileDim?.vehicleDimensionMin.width ||
        this.data?.Profile.totalLength <
          profileDim?.vehicleDimensionMin.length) &&
      !this.isEuRegion
    ) {
      this.data.Profile.vehicleType = ProfileVehicleType.MidsizeTruck;
      this.exceedVehicleType =
        this.conversionService.convertToProfileVehicleTypeEnum(
          this.data?.Profile?.vehicleType
        );
      const availableProfileByVehicleType =
        this.profileService.getAvailableProfileByVehicleType(
          this.data?.Profile?.vehicleType
        );
      this.data.Profile.name = availableProfileByVehicleType?.name;
    }
    this.existingProfileName = this.data?.Profile?.name;
  }

  matchProfile(isEuRegion: boolean) {
    const routingProfileId = this.profileService.getProfileId(
      this.data?.Profile?.name
    );
    const profileData = this.profileService.getProfile(routingProfileId);
    if (
      this.data?.Profile?.unitsOfMeasure === VehicleDimUnits.Metric &&
      this.data?.Profile?.totalLength &&
      this.data?.Profile?.vehicleType !== ProfileVehicleType.Auto
    ) {
      const DimOptions = this.conversionService.convertDimensionMeterToInches(
        this.data?.Profile
      );
      this.data.Profile.maxHeight = Number(DimOptions.trkHeight);
      this.data.Profile.totalLength = Number(DimOptions.trkLength);
      this.data.Profile.maxWidth = Number(DimOptions.trkWidth);
      this.data.Profile.totalWeight = Number(DimOptions.trkWeight);
    }
    if (
      this.isEuRegion &&
      this.data?.Profile?.totalLength &&
      this.data?.Profile?.vehicleType !== ProfileVehicleType.Auto
    ) {
      const DimOptions =
        this.conversionService.convertDimensionInchesToCentimeter(
          this.data?.Profile
        );
      this.data.Profile.maxHeight = Number(DimOptions.trkHeight);
      this.data.Profile.totalLength = Number(DimOptions.trkLength);
      this.data.Profile.maxWidth = Number(DimOptions.trkWidth);
      this.data.Profile.totalWeight = Number(DimOptions.trkWeight);
    }

    const routeProfileOption: RoutingProfileModel =
      this.conversionService.convertToRoutingProfileModal(
        this.data,
        profileData,
        isEuRegion
      );
    const profiles: RoutingProfileModel[] = this.profileService.getProfiles();
    const matchedProfile = profiles.find(
      (profile) =>
        this.matchRouteOptions(routeProfileOption, profile) &&
        this.matchVehicleOptions(routeProfileOption, profile)
    );
    if (matchedProfile) {
      this.existingProfileName = matchedProfile?.name;
      this.data.Profile.name = matchedProfile?.name;
      this.data.Profile.vehicleType =
        this.conversionService.convertToProfileVehicleType(
          matchedProfile?.vehicleType
        );
    } else {
      this.checkProfileDimensionsLimit();
    }
    this.isLoading = false;
  }

  matchRouteOptions(
    routeProfileOption: RoutingProfileModel,
    profile: RoutingProfileModel
  ): boolean {
    const routeOption = routeProfileOption?.routingOptions;
    const profileRouteOption = profile?.routingOptions;
    let isMatch = true;
    if (
      routeOption?.ferries === profileRouteOption?.ferries &&
      routeOption?.routeType === profileRouteOption?.routeType &&
      routeOption?.sideOfStreetAdherenceLevel ===
        profileRouteOption?.sideOfStreetAdherenceLevel &&
      routeOption?.tollRoads === profileRouteOption?.tollRoads
    ) {
      if (
        routeOption?.elevationLimit &&
        routeProfileOption.vehicleType !== VehicleTypeEnum.Auto &&
        routeProfileOption.vehicleType !== VehicleTypeEnum.LightDuty &&
        !this.isEuRegion
      ) {
        isMatch =
          routeOption?.elevationLimit === profileRouteOption?.elevationLimit;
      }
      if (
        routeProfileOption.vehicleType !== VehicleTypeEnum.Auto &&
        routeProfileOption.vehicleType !== VehicleTypeEnum.LightDuty &&
        !this.isEuRegion &&
        isMatch
      ) {
        isMatch =
          routeOption?.useStateAndNationalNetwork ===
          profileRouteOption?.useStateAndNationalNetwork;
      }
      if (!this.isEuRegion && isMatch) {
        isMatch =
          routeOption?.useOpenBorders === profileRouteOption?.useOpenBorders;
      }

      return isMatch;
    }

    return false;
  }
  matchVehicleOptions(
    routeProfileOption: RoutingProfileModel,
    profile: RoutingProfileModel
  ): boolean {
    const vehicleOptions = routeProfileOption?.vehicleOptions;
    const profileVehicleOptions = profile?.vehicleOptions;
    let isMatch = true;
    if (routeProfileOption.vehicleType !== VehicleTypeEnum.Auto) {
      if (
        vehicleOptions?.vehicleConfig?.height ===
          profileVehicleOptions?.vehicleConfig?.height &&
        vehicleOptions?.vehicleConfig?.weight ===
          profileVehicleOptions?.vehicleConfig?.weight &&
        vehicleOptions?.vehicleConfig?.width ===
          profileVehicleOptions?.vehicleConfig?.width &&
        vehicleOptions?.vehicleConfig?.length ===
          profileVehicleOptions?.vehicleConfig?.length
      ) {
        if (
          !this.isEuRegion &&
          routeProfileOption.vehicleType !== VehicleTypeEnum.LightDuty
        ) {
          isMatch =
            vehicleOptions?.hazmatType === profileVehicleOptions?.hazmatType;
        }
        if (vehicleOptions?.governorSpeedLimit && isMatch) {
          isMatch =
            vehicleOptions?.governorSpeedLimit ===
            profileVehicleOptions?.governorSpeedLimit;
        }
        return isMatch;
      }
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }
}
